import ArticlePrintContent from "./article-print-content";
import {useCallback, useEffect, useRef} from "react";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import {ArticleDetails} from "../../core/models/interfaces/article-details.interface";
import {useReactToPrint} from "react-to-print";
import {printPageStyle} from "../constants/react-to-print-page-style";
import {useLoading} from "../../core/hooks/use-loading.hook";
import {UniversalClickHandler} from "@common/models/types/universal-click-handler.type";

export interface ArticleToPrintModelData {
    articleData?: ArticleDetails;
    onPrint?: UniversalClickHandler;
}

const ArticleToPrintModal = () => {
    const {data, hideModal} = useModalWithData<ArticleToPrintModelData>(ModalIds.PRINT_ARTICLE);
    const article: ArticleDetails = data?.articleData!;
    const printContentRef = useRef<HTMLDivElement>(null);
    const {stopLoading} = useLoading();

    const waitForImagesAndFontsInDoc = useCallback(async (doc: Document) => {
        const images = Array.from(doc.querySelectorAll('img')) as HTMLImageElement[];

        const imagePromises = images.map(img => {
            if (img.complete && img.naturalHeight !== 0) return Promise.resolve();

            if (typeof (img as any).decode === 'function') {
                return (img as any).decode().catch(() => void 0);
            }

            return new Promise<void>((resolve) => {
                const cleanup = () => {
                    img.removeEventListener('load', onLoad);
                    img.removeEventListener('error', onError);
                };
                const onLoad = () => {
                    cleanup();
                    resolve();
                };
                const onError = () => {
                    cleanup();
                    resolve();
                };

                img.addEventListener('load', onLoad, {once: true});
                img.addEventListener('error', onError, {once: true});
            });
        });

        const fontsPromise = doc.fonts?.ready ?? Promise.resolve();

        await Promise.all([Promise.all(imagePromises), fontsPromise]);
    }, []);

    const handlePrint = useReactToPrint({
        contentRef: printContentRef,
        documentTitle: "Zdrowio",
        pageStyle: printPageStyle,
        print: async (iframe) => {
            const doc = iframe.contentDocument;
            if (!doc) return;
            await waitForImagesAndFontsInDoc(doc);
            stopLoading("articleToPrintPrepare");
            data?.onPrint && data.onPrint();
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        },
        onAfterPrint: () => hideModal(),
    });

    useEffect(() => {
        if (data && printContentRef.current && article) {
            // Daj czas na załadowanie zdjęć przed drukowaniem
            const timer = setTimeout(() => {
                handlePrint();
            }, 500);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [data, article, handlePrint]);

    if (!article) return null;

    return (
        <div style={{position: 'absolute', left: '-9999px', top: 0, visibility: 'hidden', pointerEvents: 'none'}}>
            <ArticlePrintContent article={article} ref={printContentRef}/>
        </div>
    );
}

export default ArticleToPrintModal
